/**
 * Google Analytics (gtag.js) integration for StorySplat Viewer.
 */

import type { ViewerEvent, ViewerInstance } from '../types';

type ViewerEventCallback = Parameters<ViewerInstance['on']>[1];

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GOOGLE_TAG_ID_PATTERN = /^G-[A-Z0-9]{6,20}$/i;

export type GoogleAnalyticsHandle = {
  destroy: () => void;
  /**
   * Re-bind to the viewer a portal teleport created, and re-point scene
   * attribution at the destination. Lets `createViewer` forward one GA
   * instance across teleports instead of re-running setup (which re-fired
   * `gtag('config')`, an extra `page_view`, and another `beforeunload`
   * handler on every hop).
   */
  attachViewer: (viewer: ViewerInstance, sceneMetadata?: { sceneId?: string; sceneName?: string }) => void;
};

export function isValidGoogleTagId(value: unknown): value is string {
  return typeof value === 'string' && GOOGLE_TAG_ID_PATTERN.test(value.trim());
}

function normalizeGoogleTagId(value: unknown): string | null {
  return isValidGoogleTagId(value) ? value.trim().toUpperCase() : null;
}

function safeString(value: unknown, fallback = 'unknown', maxLength = 100): string {
  const normalized = String(value || fallback).trim();
  return (normalized || fallback).slice(0, maxLength);
}

function injectGtag(measurementId: string): void {
  const scriptId = `storysplat-ga-${measurementId.replace(/[^A-Z0-9_-]/gi, '-')}`;

  if (!document.getElementById(scriptId)) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
  });
}

/**
 * Set up Google Analytics event tracking on the viewer.
 */
export function setupGoogleAnalytics(
  viewer: ViewerInstance,
  measurementId: string,
  sceneMetadata?: { sceneId?: string; sceneName?: string }
): GoogleAnalyticsHandle {
  const noop: GoogleAnalyticsHandle = { destroy: () => undefined, attachViewer: () => undefined };
  const safeMeasurementId = normalizeGoogleTagId(measurementId);
  if (!safeMeasurementId || typeof window === 'undefined' || typeof document === 'undefined') {
    return noop;
  }

  injectGtag(safeMeasurementId);
  const gtag = window.gtag;
  if (typeof gtag !== 'function') {
    return noop;
  }

  const sessionStart = Date.now();
  // Mutable so a portal teleport re-points attribution without a second
  // gtag('config') / beforeunload registration. See GoogleAnalyticsHandle.
  let activeViewer = viewer;
  let sceneId = safeString(sceneMetadata?.sceneId, '', 128);
  let sceneName = safeString(sceneMetadata?.sceneName || document.title, '', 120);
  const bindings: Array<{ viewer: ViewerInstance; event: ViewerEvent; callback: ViewerEventCallback }> = [];

  const observe = (event: ViewerEvent, callback: ViewerEventCallback) => {
    const target = activeViewer;
    const on = target.onInternal ? target.onInternal.bind(target) : target.on.bind(target);
    on(event, callback);
    bindings.push({ viewer: target, event, callback });
  };

  const unobserveAll = () => {
    for (const binding of bindings) {
      try { binding.viewer.off(binding.event, binding.callback); } catch { /* viewer already destroyed */ }
    }
    bindings.length = 0;
  };

  const sendPageView = () => {
    gtag('event', 'page_view', {
      page_title: sceneName || document.title,
      storysplat_scene_id: sceneId,
      storysplat_scene_name: sceneName,
    });
  };

  sendPageView();

  const bindViewerEvents = () => {
    observe('waypointChange', (data?: { waypoint?: { name?: string }; name?: string; index?: number }) => {
      gtag('event', 'storysplat_waypoint', {
        waypoint_name: safeString(data?.waypoint?.name || data?.name),
        waypoint_index: data?.index ?? 0,
        storysplat_scene_id: sceneId,
      });
    });

    observe('hotspotClick', (data?: { hotspot?: { title?: string; name?: string; id?: string } }) => {
      gtag('event', 'storysplat_hotspot', {
        hotspot_name: safeString(data?.hotspot?.title || data?.hotspot?.name),
        hotspot_id: safeString(data?.hotspot?.id, '', 128),
        storysplat_scene_id: sceneId,
      });
    });

    observe('modeChange', (data?: { mode?: string }) => {
      gtag('event', 'storysplat_mode_change', {
        camera_mode: safeString(data?.mode),
        storysplat_scene_id: sceneId,
      });
    });

    observe('portalActivated', (data?: { targetSceneName?: string; targetSceneId?: string }) => {
      gtag('event', 'storysplat_portal', {
        portal_target: safeString(data?.targetSceneName),
        portal_target_id: safeString(data?.targetSceneId, '', 128),
        storysplat_scene_id: sceneId,
      });
    });

    observe('playbackStart', () => {
      gtag('event', 'storysplat_playback', {
        action: 'start',
        storysplat_scene_id: sceneId,
      });
    });

    observe('playbackStop', () => {
      gtag('event', 'storysplat_playback', {
        action: 'stop',
        storysplat_scene_id: sceneId,
      });
    });

    observe('playbackComplete', () => {
      gtag('event', 'storysplat_playback', {
        action: 'complete',
        storysplat_scene_id: sceneId,
      });
    });
  };

  bindViewerEvents();

  const handleUnload = () => {
    const durationSec = Math.round((Date.now() - sessionStart) / 1000);
    gtag('event', 'storysplat_engagement', {
      session_duration: durationSec,
      storysplat_scene_id: sceneId,
    });
  };
  window.addEventListener('beforeunload', handleUnload);

  return {
    attachViewer: (nextViewer: ViewerInstance, nextMetadata?: { sceneId?: string; sceneName?: string }) => {
      unobserveAll();
      activeViewer = nextViewer;
      if (nextMetadata) {
        sceneId = safeString(nextMetadata.sceneId, '', 128);
        sceneName = safeString(nextMetadata.sceneName || document.title, '', 120);
      }
      bindViewerEvents();
      // One page_view per scene entered — the teleport really is a new page in
      // GA's model. What must NOT repeat is gtag('config') and the unload
      // handler, and neither does here.
      sendPageView();
    },
    destroy: () => {
      window.removeEventListener('beforeunload', handleUnload);
      unobserveAll();
    },
  };
}
